// Shared cover-art definitions + PNG export for the Book Cover Studio,
// Storybook Vault, and public share cards. The Collector's Frame (golden
// foil/prism border) and the embossed "Golden Star Author Edition" corner
// seal auto-apply whenever a rare holographic sticker is on the cover —
// both in the live DOM render and in the exported PNG.
import { createElement } from "react";
import ReactDOMServer from "react-dom/server";
import { Bird, BookOpen, Crown, Feather, Flame, Heart, Rocket, Smile, Sparkles, Star } from "lucide-react";

export const ICONS = {
  sticker_comet: Sparkles, sticker_crown: Crown, sticker_dragon: Flame,
  holo_quill: Feather, holo_crown: Crown, holo_pip: Bird,
  starter_pip: Bird, starter_star: Star, starter_heart: Heart,
  starter_rocket: Rocket, starter_book: BookOpen, starter_smiley: Smile,
};

// Starter Sticker Kit: permanently unlocked for every child — no purchase
// or streak needed. Rendered under its own tray header in the Cover Studio.
export const STARTER_STICKERS = [
  { sid: "starter_pip", name: "Pip", rare: false },
  { sid: "starter_star", name: "Star", rare: false },
  { sid: "starter_heart", name: "Heart", rare: false },
  { sid: "starter_rocket", name: "Rocket", rare: false },
  { sid: "starter_book", name: "Book", rare: false },
  { sid: "starter_smiley", name: "Smiley", rare: false },
];

export const BGS = {
  honey: { label: "Honey", css: "linear-gradient(160deg, #b45309 0%, #f59e0b 55%, #fde68a 100%)", stops: ["#b45309", "#f59e0b", "#fde68a"] },
  meadow: { label: "Meadow", css: "linear-gradient(160deg, #14532d 0%, #16a34a 60%, #bef264 100%)", stops: ["#14532d", "#16a34a", "#bef264"] },
  night: { label: "Night", css: "linear-gradient(160deg, #0b1026 0%, #1e293b 60%, #475569 100%)", stops: ["#0b1026", "#1e293b", "#475569"] },
  galaxy: { label: "Galaxy", css: "linear-gradient(160deg, #2e1065 0%, #6d28d9 55%, #c084fc 100%)", stops: ["#2e1065", "#6d28d9", "#c084fc"] },
};

export const hasRare = (cover) => (cover?.stickers || []).some((s) => s.rare);

const iconImage = (sid, rare) => new Promise((resolve) => {
  const Comp = ICONS[sid] || Sparkles;
  const svg = ReactDOMServer.renderToStaticMarkup(createElement(Comp, {
    color: rare ? "#fcd34d" : "#ffffff",
    fill: rare ? "rgba(251,191,36,0.6)" : "rgba(255,255,255,0.25)",
    strokeWidth: 2, size: 96,
  }));
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = "data:image/svg+xml;base64," + btoa(svg);
});

export async function renderCoverPng(cover) {
  const W = 600, H = 800, rare = hasRare(cover);
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const ctx = cv.getContext("2d");
  const stops = (BGS[cover?.bg] || BGS.honey).stops;
  const g = ctx.createLinearGradient(0, 0, W * 0.35, H);
  stops.forEach((c, i) => g.addColorStop(i / (stops.length - 1), c));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  // Title
  ctx.textAlign = "center";
  ctx.fillStyle = "#ffffff";
  ctx.shadowColor = "rgba(0,0,0,0.6)"; ctx.shadowBlur = 8; ctx.shadowOffsetY = 3;
  ctx.font = "800 44px 'Baloo 2', 'Comic Sans MS', sans-serif";
  ctx.fillText((cover?.title || "").slice(0, 40), W / 2, 92, W - 90);
  ctx.shadowColor = "transparent"; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
  // Stickers
  for (const s of cover?.stickers || []) {
    const img = await iconImage(s.sid, s.rare);
    if (!img) continue;
    ctx.save();
    ctx.translate((s.x / 100) * W, (s.y / 100) * H);
    ctx.rotate((s.rot * Math.PI) / 180);
    const size = 100 * (s.scale || 1);
    if (s.rare) { ctx.shadowColor = "rgba(251,191,36,0.95)"; ctx.shadowBlur = 26; }
    ctx.drawImage(img, -size / 2, -size / 2, size, size);
    ctx.restore();
  }
  if (rare) {
    // Collector's Frame: golden foil / prism border
    const fg = ctx.createLinearGradient(0, 0, W, H);
    const foil = ["#b45309", "#fde68a", "#ffffff", "#86efac", "#93c5fd", "#fde68a", "#b45309"];
    foil.forEach((c, i) => fg.addColorStop(i / (foil.length - 1), c));
    ctx.strokeStyle = fg; ctx.lineWidth = 18;
    ctx.strokeRect(9, 9, W - 18, H - 18);
    ctx.strokeStyle = "rgba(255,255,255,0.9)"; ctx.lineWidth = 2;
    ctx.strokeRect(21, 21, W - 42, H - 42);
    // Embossed corner seal: gold medal + ribbons + edition banner
    const cx = W - 104, cy = H - 148;
    ctx.fillStyle = "#b45309";
    ctx.beginPath(); ctx.moveTo(cx - 26, cy + 22); ctx.lineTo(cx - 42, cy + 78); ctx.lineTo(cx - 12, cy + 60); ctx.closePath(); ctx.fill();
    ctx.beginPath(); ctx.moveTo(cx + 26, cy + 22); ctx.lineTo(cx + 42, cy + 78); ctx.lineTo(cx + 12, cy + 60); ctx.closePath(); ctx.fill();
    const mg = ctx.createRadialGradient(cx - 12, cy - 12, 6, cx, cy, 46);
    mg.addColorStop(0, "#fef3c7"); mg.addColorStop(0.55, "#f59e0b"); mg.addColorStop(1, "#92400e");
    ctx.beginPath(); ctx.arc(cx, cy, 42, 0, Math.PI * 2);
    ctx.fillStyle = mg; ctx.fill();
    ctx.lineWidth = 3; ctx.strokeStyle = "#fde68a"; ctx.stroke();
    ctx.fillStyle = "#fffbeb";
    ctx.beginPath();
    for (let i = 0; i < 10; i++) {
      const r = i % 2 === 0 ? 20 : 9;
      const a = (Math.PI / 5) * i - Math.PI / 2;
      const fn = i === 0 ? "moveTo" : "lineTo";
      ctx[fn](cx + r * Math.cos(a), cy + r * Math.sin(a));
    }
    ctx.closePath(); ctx.fill();
    const bw = 236, bh = 28, bx = W - 36 - bw, by = H - 56;
    ctx.fillStyle = "#92400e";
    if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(bx, by, bw, bh, 8); ctx.fill(); }
    else ctx.fillRect(bx, by, bw, bh);
    ctx.strokeStyle = "#fde68a"; ctx.lineWidth = 2; ctx.strokeRect(bx, by, bw, bh);
    ctx.fillStyle = "#fef3c7";
    ctx.font = "800 15px 'Baloo 2', sans-serif";
    ctx.fillText("GOLDEN STAR AUTHOR EDITION", bx + bw / 2, by + 19);
  }
  return cv.toDataURL("image/png");
}

export function downloadPng(dataUrl, name) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = name;
  a.click();
}
